import {
  type AcceptedPromotionalAdImageMime,
  type PromotionalAdImageValidationError,
} from '../types/promotional-ad';

export const MAX_PROMOTIONAL_AD_IMAGE_BYTES = 4 * 1024 * 1024;
export const PROMOTIONAL_AD_IMAGE_MIN_WIDTH = 600;
export const PROMOTIONAL_AD_IMAGE_MAX_WIDTH = 2400;
export const PROMOTIONAL_AD_IMAGE_MIN_HEIGHT = 200;
export const PROMOTIONAL_AD_IMAGE_MAX_HEIGHT = 1200;
export const PROMOTIONAL_AD_MIN_ASPECT_RATIO = 1.8;
export const PROMOTIONAL_AD_MAX_ASPECT_RATIO = 4;

export const ACCEPTED_PROMOTIONAL_AD_IMAGE_MIME = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
} as const;

export interface PromotionalAdByteInspectionResult {
  readonly mimeType: AcceptedPromotionalAdImageMime;
  readonly width: number;
  readonly height: number;
}

const JPEG_SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.slice(offset, offset + length));
}

function readU16Le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) + ((bytes[offset + 1] ?? 0) << 8);
}

function readU16Be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) << 8) + (bytes[offset + 1] ?? 0);
}

function readU24Le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) + ((bytes[offset + 1] ?? 0) << 8) + ((bytes[offset + 2] ?? 0) << 16);
}

function readU32Be(bytes: Uint8Array, offset: number): number {
  return (
    (bytes[offset] ?? 0) * 0x1000000 +
    (((bytes[offset + 1] ?? 0) << 16) | ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0))
  );
}

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  return signature.every((value, index) => (bytes[index] ?? -1) === value);
}

function looksScriptable(bytes: Uint8Array): boolean {
  const head = readAscii(bytes, 0, Math.min(bytes.length, 256)).trimStart().toLowerCase();
  return (
    head.startsWith('<svg') ||
    head.startsWith('<?xml') ||
    head.startsWith('<!doctype html') ||
    head.startsWith('<html') ||
    head.startsWith('{') ||
    head.startsWith('[') ||
    head.startsWith('%pdf') ||
    head.startsWith('pk')
  );
}

function parsePng(bytes: Uint8Array): PromotionalAdByteInspectionResult | null {
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length < 24 || !startsWith(bytes, pngSignature) || readAscii(bytes, 12, 4) !== 'IHDR') {
    return null;
  }
  return { mimeType: 'image/png', width: readU32Be(bytes, 16), height: readU32Be(bytes, 20) };
}

function parseGif(bytes: Uint8Array): PromotionalAdByteInspectionResult | null {
  if (bytes.length < 10) return null;
  const header = readAscii(bytes, 0, 6);
  if (header !== 'GIF87a' && header !== 'GIF89a') return null;
  return { mimeType: 'image/gif', width: readU16Le(bytes, 6), height: readU16Le(bytes, 8) };
}

function parseJpeg(bytes: Uint8Array): PromotionalAdByteInspectionResult | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let offset = 2;
  while (offset + 3 < bytes.length) {
    if ((bytes[offset] ?? 0) !== 0xff) {
      offset += 1;
      continue;
    }
    while ((bytes[offset] ?? 0) === 0xff) offset += 1;
    const marker = bytes[offset] ?? 0;
    offset += 1;
    if (marker === 0xd9 || marker === 0xda) break;
    if (offset + 1 >= bytes.length) return null;
    const length = readU16Be(bytes, offset);
    if (length < 2 || offset + length > bytes.length) return null;
    if (JPEG_SOF_MARKERS.has(marker)) {
      if (length < 7) return null;
      return {
        mimeType: 'image/jpeg',
        height: readU16Be(bytes, offset + 3),
        width: readU16Be(bytes, offset + 5),
      };
    }
    offset += length;
  }
  return null;
}

function parseWebp(bytes: Uint8Array): PromotionalAdByteInspectionResult | null {
  if (bytes.length < 16 || readAscii(bytes, 0, 4) !== 'RIFF' || readAscii(bytes, 8, 4) !== 'WEBP') {
    return null;
  }
  const chunk = readAscii(bytes, 12, 4);
  if (chunk === 'VP8X' && bytes.length >= 30) {
    return {
      mimeType: 'image/webp',
      width: readU24Le(bytes, 24) + 1,
      height: readU24Le(bytes, 27) + 1,
    };
  }
  if (chunk === 'VP8 ' && bytes.length >= 30 && bytes[23] === 0x9d && bytes[24] === 0x01) {
    return {
      mimeType: 'image/webp',
      width: readU16Le(bytes, 26) & 0x3fff,
      height: readU16Le(bytes, 28) & 0x3fff,
    };
  }
  if (chunk === 'VP8L' && bytes.length >= 25 && bytes[20] === 0x2f) {
    const b0 = bytes[21] ?? 0;
    const b1 = bytes[22] ?? 0;
    const b2 = bytes[23] ?? 0;
    const b3 = bytes[24] ?? 0;
    return {
      mimeType: 'image/webp',
      width: 1 + (((b1 & 0x3f) << 8) | b0),
      height: 1 + (((b3 & 0x0f) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6)),
    };
  }
  return null;
}

function dimensionsAreValid(width: number, height: number): boolean {
  if (
    width < PROMOTIONAL_AD_IMAGE_MIN_WIDTH ||
    width > PROMOTIONAL_AD_IMAGE_MAX_WIDTH ||
    height < PROMOTIONAL_AD_IMAGE_MIN_HEIGHT ||
    height > PROMOTIONAL_AD_IMAGE_MAX_HEIGHT
  ) {
    return false;
  }
  const aspect = width / height;
  return aspect >= PROMOTIONAL_AD_MIN_ASPECT_RATIO && aspect <= PROMOTIONAL_AD_MAX_ASPECT_RATIO;
}

export function inspectPromotionalAdImageBytes(
  bytes: Uint8Array,
  declaredMime: string | null | undefined,
): PromotionalAdByteInspectionResult | PromotionalAdImageValidationError {
  if (bytes.length === 0) return 'imageRequired';
  if (bytes.length > MAX_PROMOTIONAL_AD_IMAGE_BYTES || looksScriptable(bytes)) {
    return bytes.length > MAX_PROMOTIONAL_AD_IMAGE_BYTES ? 'imageTooLarge' : 'imageInvalidType';
  }
  if (
    !declaredMime ||
    !Object.prototype.hasOwnProperty.call(ACCEPTED_PROMOTIONAL_AD_IMAGE_MIME, declaredMime)
  ) {
    return 'imageInvalidType';
  }

  const parsed = parsePng(bytes) ?? parseGif(bytes) ?? parseJpeg(bytes) ?? parseWebp(bytes);
  if (!parsed || parsed.mimeType !== declaredMime) return 'imageInvalidType';
  if (!dimensionsAreValid(parsed.width, parsed.height)) return 'imageDimensionsInvalid';
  return parsed;
}
